import { useEffect, useMemo, useRef, useState } from 'react'

import { NAV, type Route } from '../../app/nav'
import { useSession } from '../../lib/session'
import { cn } from '../../lib/utils'
import { Icon } from '../icon'

/**
 * The command palette.
 *
 * Opened with `/` or from the top bar's search box. It navigates; it does not search records —
 * there is no cross-entity search endpoint, and a box that sometimes finds a contact and sometimes
 * only finds a page is a box nobody trusts. When that endpoint exists this is where it lands, and
 * the results list is already the right shape for it.
 *
 * Fully keyboard-driven, because a palette you have to reach for the mouse inside has failed at
 * the one thing it is for.
 */
export function CommandPalette({
  open,
  onClose,
  onNavigate,
}: {
  open: boolean
  onClose: () => void
  onNavigate: (route: Route) => void
}) {
  const { isPlatformStaff } = useSession()
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const results = useMemo(
    () =>
      NAV.filter(
        (entry) =>
          // Same rule as the sidebar: a customer must not be offered a jump to a screen that
          // exists only to tell them it is not theirs.
          (isPlatformStaff || !entry.platformOnly) &&
          (!query || entry.label.toLowerCase().includes(query.toLowerCase())),
      ),
    [query, isPlatformStaff],
  )

  useEffect(() => {
    if (open) {
      setQuery('')
      setIndex(0)
      // A frame later, so focus lands after the element is in the document.
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    setIndex(0)
  }, [query])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') return onClose()
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setIndex((i) => Math.min(results.length - 1, i + 1))
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setIndex((i) => Math.max(0, i - 1))
      }
      if (event.key === 'Enter') {
        event.preventDefault()
        const chosen = results[index]
        if (chosen) onNavigate(chosen.id)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, results, index, onNavigate, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[12vh]">
      <button
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="bg-overlay animate-fade absolute inset-0"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search everything"
        className="bg-panel border-line animate-fade relative w-full max-w-[520px] overflow-hidden rounded-[11px] border"
      >
        <div className="border-line flex items-center gap-2.5 border-b px-3.5 py-3">
          <Icon name="search" size={15} className="text-faint" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search everything"
            aria-label="Search everything"
            className="text-text placeholder:text-faint min-w-0 flex-1 border-0 bg-transparent text-[13.5px] outline-none"
          />
          <kbd className="border-line text-faint rounded-[4px] border px-1.5 py-px text-[10px]">
            Esc
          </kbd>
        </div>

        <div className="max-h-[46vh] overflow-y-auto p-1.5">
          {results.length === 0 ? (
            <p className="text-faint flex items-center justify-center gap-2 px-2.5 py-6 text-center text-[12.5px]">
              <Icon name="noResults" size={15} />
              Nothing matches “{query}”.
            </p>
          ) : (
            results.map((entry, position) => (
              <button
                key={entry.id}
                onMouseEnter={() => setIndex(position)}
                onClick={() => onNavigate(entry.id)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-[7px] px-2.5 py-2 text-left text-[13px] transition-colors',
                  position === index ? 'bg-accent-soft text-accent' : 'text-muted',
                )}
              >
                <Icon name={entry.icon} />
                <span className="flex-1 truncate">{entry.label}</span>
                {!entry.wired && (
                  <span className="text-faint text-[10.5px]">no endpoint yet</span>
                )}
              </button>
            ))
          )}
        </div>

        <div className="border-line bg-panel2 text-faint flex items-center gap-3 border-t px-3.5 py-2 text-[11px]">
          <span className="flex items-center gap-1">
            <Icon name="arrowUp" size={11} />
            <Icon name="arrowDown" size={11} />
            to move
          </span>
          <span className="flex items-center gap-1">
            <Icon name="enter" size={11} />
            to open
          </span>
          <span className="ml-auto">Pages only — record search has no endpoint yet</span>
        </div>
      </div>
    </div>
  )
}
